// IS BUILDING FOR PRODUCTION
export const isProductionBuild = process.env.NODE_ENV === "production";
export const isDevelopmentBuild = process.env.NODE_ENV === "development";

// API ENDPOINT, BE AWARE OF DANGER
export const isMain = process.env.CI_COMMIT_REF_NAME?.trim() === "main";
export const isStaging = process.env.CI_COMMIT_REF_NAME?.trim() === "staging";
export const isDev = process.env.CI_COMMIT_REF_NAME?.trim() === "develop";

/* Tooling setup  */
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import isLeapYear from "dayjs/plugin/isLeapYear";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import RelativeTime from "dayjs/plugin/relativeTime";
import UTC from "dayjs/plugin/utc";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isLeapYear);
dayjs.extend(LocalizedFormat);
dayjs.extend(RelativeTime);
dayjs.extend(UTC);
dayjs.extend(isBetween);

import * as ReactGA from "react-ga";
import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { getAmplitudeContextProperties } from "./amplitude-context-properties";

function getEnvFromUrl(): string {
	if (typeof window === "undefined") return "local";

	const hostname = window.location.hostname;

	if (hostname === "dashboard.zigpay.com.br") return "production";
	if (hostname === "staging.dashboard.zigpay.com.br") return "staging";
	if (hostname === "develop.dashboard.zigpay.com.br") return "develop";

	return "local";
}

function setupTracking() {
	if (isProductionBuild) {
		console.log("VERSION", process.env.VITE_APP_VERSION);

		ReactGA.initialize("UA-52146804-2", {});
		ReactGA.set({ env: process.env.NODE_ENV || "development" });
	}
}

function setupAmplitude() {
	if (process.env.AMPLITUDE_API_KEY && process.env.AMPLITUDE_SERVER_URL) {
		const sessionReplayTracking = sessionReplayPlugin();

		amplitude.add({
			execute: async (event: amplitude.Types.Event) => {
				const contextProperties = getAmplitudeContextProperties();

				const env = getEnvFromUrl();

				event.event_properties = {
					...contextProperties,
					...event.event_properties,
					env,
					project: "Dashboard Z",
				};

				return event;
			},
		});

		amplitude.add(sessionReplayTracking);

		amplitude.init(process.env.AMPLITUDE_API_KEY, {
			autocapture: true,
			transport: "beacon",
			flushIntervalMillis: 0,
			flushQueueSize: 1,
			defaultTracking: true,
			serverUrl: process.env.AMPLITUDE_SERVER_URL || undefined,
		});
	}
}

function setupServiceWorker() {
	const currentVersion = process.env.VITE_APP_VERSION ?? "development";
	try {
		const storedVersion = localStorage.getItem("build");
		localStorage.setItem("build", currentVersion);
		if (storedVersion !== null && storedVersion !== currentVersion) {
			window.location.reload();
		}
	} catch {
		// localStorage not available, VitePWA handles SW updates automatically
	}
}

function setupLocale() {
	const DEFAULT_LANGUAGE = "pt-BR";
	const locale = localStorage.getItem("lng") ?? DEFAULT_LANGUAGE;

	if (locale === "en-US") {
		dayjs.locale("en");
		return;
	}

	if (locale === "fr-FR") {
		require("dayjs/locale/fr");
		dayjs.locale("fr");
		return;
	}

	if (locale === "es-MX" || locale === "es-ES") {
		require("dayjs/locale/es");
		dayjs.locale("es");
		return;
	}

	import("dayjs/locale/pt-br");
	dayjs.locale("pt-br");
}

export async function setupAppConfiguration() {
	setupLocale();
	setupTracking();
	setupAmplitude();

	if (isProductionBuild) {
		setupServiceWorker();
	}
}
